import * as Blockly from 'blockly/core';
import { BOARD_CONFIG } from '@/(routers)/boards/editor/block/blockConfig';
import {
    getHelperFunctions,
    getLoopCodes,
    getSetupCodes,
    getWireBeginCode,
    resetHelperFunctions,
    resetLoopCodes,
    resetSetupCodes,
    resetWireBegin,
} from '@/(routers)/boards/editor/block/generators/arduino/_gen';
import {
    irDefineCode,
    irIncludeCode,
    irMatchRawBlueUsed,
    irMatchRawHelperCode,
    irMatchRawHelperCodeBlue,
    irMatchRawUsed,
    irRecvDefineCodes,
    irSendDefineCodes,
    irSendRawHelperCode,
    irUsed,
    resetIr,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/action/helper/ir_helper';
import {
    ledBarGlobalCodes,
    resetLedBar,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/action/led_bar';
import {
    resetRgbLed,
    rgbLedHelperCodes,
    rgbLedSetupCodes,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/action/rgb_led';
import {
    resetServo,
    servoGlobalCodes,
    servoUsed,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/action/servo_180';
import {
    getOledJpFontCode,
    oledUsed,
    resetOled,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/display/oled_print';
import {
    resetSevenSeg,
    sevenSegGlobalCodes,
    sevenSegHelperCode,
    sevenSegUsed,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/display/seven_seg_numer';
import {
    getSdcardGlobalCode,
    resetSdcard,
    sdcardUsed,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/file/helper/sdcard_helper';
import {
    funcDefinitionCodes,
    resetFuncDefinitions,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/function/func';
import {
    gpsGlobalCodes,
    gpsUsed,
    resetGps,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/gps/helper/gps_helper';
import {
    barometricPressureGlobalCodes,
    barometricPressureUsed,
    resetBarometricPressure,
    seaLevelPressureHelperCode,
    seaLevelPressureUsed,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/sensing/barometric_pressure';
import {
    brightnessHelperCode,
    brightnessUsed,
    resetBrightness,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/sensing/brightness';
import {
    accelUsed,
    accelerometerHelperCode,
    accelerometerSetupCode,
    angleUsed,
    resetAccelerometer,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/sensing/helper/accelerometer_helper';
import {
    percentHelperCode,
    percentUsed,
    resetPercent,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/sensing/helper/analog_helper';
import {
    humidityGlobalCodes,
    humidityHelperCode,
    humidityUsed,
    resetHumidity,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/sensing/humidity';
import {
    resetTemperature,
    temperatureGlobalCodes,
    temperatureHelperCode,
    temperatureUsed,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/sensing/temperature';
import {
    resetWaterFlowMeasurement,
    waterFlowMeasurementGlobalCodes,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/sensing/water_flow_measurement';
import {
    resetWaterTemperature,
    waterTemperatureGlobalCodes,
    waterTemperatureHelperCode,
    waterTemperatureUsed,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/sensing/water_temperature';
import {
    btUsed,
    resetBt,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/serial/helper/monitor_bluetooth_helper';
import { variableDeclarations } from '@/(routers)/boards/editor/block/generators/arduino/blocks/variable/variable';

export function register(gen: any): void {
    gen.forBlock['schoomy_board'] = function (block: Blockly.Block): string {
        const board = block.getFieldValue('BOARD') as 'orange' | 'blue';
        const baud = BOARD_CONFIG[board].baud;

        const loopCode = (() => {
            resetPercent();
            resetBrightness();
            resetTemperature();
            resetHumidity();
            resetWaterTemperature();
            resetBarometricPressure();
            resetSevenSeg();
            resetRgbLed();
            resetAccelerometer();
            resetIr();
            resetServo();
            resetLedBar();
            resetFuncDefinitions();
            resetGps();
            resetWaterFlowMeasurement();
            resetSdcard();
            resetOled();
            resetBt();
            resetSetupCodes();
            resetLoopCodes();
            resetHelperFunctions();
            resetWireBegin();
            variableDeclarations.clear();

            return gen.statementToCode(block, 'LOOP');
        })();

        const funcBlocks = block.workspace.getBlocksByType('func', false);
        for (const funcBlock of funcBlocks) {
            gen.blockToCode(funcBlock);
        }

        const setupCode = block.workspace
            .getBlocksByType('setup', false)
            .map((b) => gen.statementToCode(b, 'SETUP'))
            .join('');

        const irDefines = irUsed() ? irDefineCode() : '';
        const tempDef = temperatureUsed() || waterTemperatureUsed() ? '#define SENSOR_BIT 9' : '';
        const humidityDef = humidityUsed() ? '#define DHT_MODEL DHT11' : '';

        const defineCode = [irDefines, tempDef, humidityDef].filter(Boolean).join('\n');

        const ledBarUsed = ledBarGlobalCodes().size > 0;

        // Wire.h は複数のブロックで使用されるため、いずれかが使われていれば1つだけ出力
        const wireUsed = accelUsed() || angleUsed() || barometricPressureUsed() || oledUsed();

        const includeCode =
            [
                '#include <SchooMyUtilities.h>',
                ledBarUsed ? '#include <Grove_LED_Bar.h>' : '',
                irUsed() ? irIncludeCode() : '',
                wireUsed ? '#include <Wire.h>' : '',
                barometricPressureUsed() ? '#include <Adafruit_BMP280.h>' : '',
                oledUsed() ? '#include <Adafruit_GFX.h>' : '',
                oledUsed() ? '#include <Adafruit_SSD1306.h>' : '',
                temperatureUsed() || waterTemperatureUsed() ? '#include <OneWire.h>' : '',
                temperatureUsed() || waterTemperatureUsed() ? '#include <DallasTemperature.h>' : '',
                humidityUsed() ? '#include <DHT.h>' : '',
                sevenSegUsed() ? '#include <TM1637Display.h>' : '',
                servoUsed() ? (board === 'orange' ? '#include <Servo.h>' : '#include <ESP32Servo.h>') : '',
                gpsUsed() ? '#include <TinyGPS++.h>' : '',
                gpsUsed() && board === 'orange' ? '#include <SoftwareSerial.h>' : '',
                sdcardUsed() ? '#include <SPI.h>' : '',
                sdcardUsed() ? '#include <SD.h>' : '',
                btUsed() ? '#include <BluetoothSerial.h>' : '',
            ]
                .filter(Boolean)
                .join('\n') + '\n';

        const irGlobalCode = irUsed() ? [...irRecvDefineCodes.values(), ...irSendDefineCodes.values()].join('') : '';

        const globalCode = [...sevenSegGlobalCodes.values()].join('');
        const ledBarGlobalCode = [...ledBarGlobalCodes().values()].join('');
        const tempGlobalCode = [...temperatureGlobalCodes.values()].join('');
        const humidityGlobalCode = [...humidityGlobalCodes.values()].join('');
        const waterTemperatureGlobalCode = [...waterTemperatureGlobalCodes.values()].join('');
        const barometricPressureGlobalCode = [...barometricPressureGlobalCodes.values()].join('');
        const servoGlobalCode = [...servoGlobalCodes().values()].join('');
        const gpsGlobalCode = [...gpsGlobalCodes.values()].join('');
        const wfOrder = ['vars_', 'setup_fn_', 'base_', 'pps_', 'total_', 'isr_'];
        const waterFlowMeasurementGlobalCode = wfOrder
            .flatMap((prefix) =>
                [...waterFlowMeasurementGlobalCodes.entries()].filter(([k]) => k.startsWith(prefix)).map(([, v]) => v),
            )
            .join('');
        const sdcardGlobal = getSdcardGlobalCode();
        const oledGlobalCode = oledUsed() ? 'Adafruit_SSD1306 oled(128, 32, &Wire, -1);\n' : '';
        const btGlobalCode = btUsed() ? 'BluetoothSerial SerialBT;\n' : '';
        const oledJpFontCode = getOledJpFontCode();
        const varGlobalCode = [...variableDeclarations.values()].join('');

        const helperCode = [
            percentUsed() ? percentHelperCode + '\n' : '',
            brightnessUsed() ? brightnessHelperCode + '\n' : '',
            temperatureUsed() ? temperatureHelperCode + '\n' : '',
            humidityUsed() ? humidityHelperCode + '\n' : '',
            waterTemperatureUsed() ? waterTemperatureHelperCode + '\n' : '',
            seaLevelPressureUsed() ? seaLevelPressureHelperCode + '\n' : '',
            sevenSegUsed() ? sevenSegHelperCode + '\n' : '',
            rgbLedHelperCodes(board) ? rgbLedHelperCodes(board) + '\n' : '',
            accelUsed() || angleUsed() ? accelerometerHelperCode(board) + '\n' : '',
            irUsed() && board === 'blue' ? irSendRawHelperCode() + '\n' : '',
            irMatchRawUsed() ? irMatchRawHelperCode() + '\n' : '',
            irMatchRawBlueUsed() ? irMatchRawHelperCodeBlue() + '\n' : '',
            getHelperFunctions() ? getHelperFunctions() + '\n' : '',
        ].join('');

        const funcDefsCode = [...funcDefinitionCodes.values()].join('\n');

        const accSetupCode = accelerometerSetupCode(board);
        const wireSetupCode = wireUsed ? `  delay(2000);\n${getWireBeginCode()}` : '';
        const pinSetupCode = [wireSetupCode, ...getSetupCodes(), rgbLedSetupCodes(board), accSetupCode].join('');
        // ユーザーがloopブロックの中に何を置いたかに関係なく、毎ループ確実に実行したいコード
        // (例: Bluetooth接続デバイス名の定期再送)。ユーザーの書いたloopCodeより前に置く。
        const loopPrologueCode = [...getLoopCodes()].join('');

        return `${
            defineCode ? defineCode + '\n' : ''
        }${includeCode}${'SchooMyUtilities scmUtils = SchooMyUtilities();\n\n'}${globalCode}${ledBarGlobalCode}${irGlobalCode}${tempGlobalCode}${humidityGlobalCode}${waterTemperatureGlobalCode}${barometricPressureGlobalCode}${servoGlobalCode}${gpsGlobalCode}${
            waterFlowMeasurementGlobalCode ? waterFlowMeasurementGlobalCode + '\n' : ''
        }${sdcardGlobal}${oledGlobalCode}${btGlobalCode}${oledJpFontCode ? oledJpFontCode + '\n' : ''}${varGlobalCode}${
            varGlobalCode ? '\n' : ''
        }${helperCode}${funcDefsCode ? funcDefsCode + '\n\n' : ''}${
            helperCode || funcDefsCode ? '\n' : ''
        }void setup() {\n  Serial.begin(${baud});\n${pinSetupCode}${setupCode}}\n\nvoid loop() {\n${loopPrologueCode}${
            loopCode || '\n'
        }}\n`;
    };
}
